"use client";

import React, { useEffect, useRef } from "react";
import { useInView, UseInViewOptions } from "motion/react";

import { cn } from "@marketing-sites/utils/cn";

import { TrackingData, TrackingAction, TrackingType } from "./types";
import { triggerAnalyticsEvent } from "./trigger-analytics-event";

export const useElementVisibleAnalytics = (
  trackingData: Required<Pick<TrackingData, "text" | "location">> &
    Omit<TrackingData, "action" | "type" | "location" | "text">,
  options: UseInViewOptions = {},
) => {
  const baseOptions: UseInViewOptions = {
    margin: "0px",
    amount: 1,
    once: true,
  };
  const finalOptions = Object.assign(baseOptions, options);
  const ref = useRef<HTMLElement | HTMLHeadingElement>(null);
  const refInView = useInView(ref, finalOptions);

  useEffect(() => {
    if (refInView) {
      triggerAnalyticsEvent({
        ...trackingData,
        action: TrackingAction.Visible,
        type: TrackingType.Element,
      });
    }
  }, [refInView, trackingData]);

  return ref;
};

export const ElementVisibleAnalytics = ({
  trackingData,
  className,
}: {
  trackingData: Required<Pick<TrackingData, "text" | "location">> &
    Omit<TrackingData, "action" | "type" | "location" | "text">;
  className?: string;
}) => {
  const ref = useElementVisibleAnalytics(trackingData);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      data-element-visible-analytics="true"
      className={cn("-z-[1000] !m-0 !p-0", className)}
    />
  );
};
